<template>
	<div class="resource-booking-modal-date-inputs">
		<div
			v-if="!isSplitMode"
			class="resource-booking-modal-date-inputs__booking-inputs"
		>
			<div class="resource-booking-modal-date-inputs__inputs-column">
				<div
					class="resource-booking-modal-date-inputs__date-select-wrapper"
				>
					<AppDatePicker
						v-tooltip="dateDisabledTooltip"
						:model-value="booking.startDate"
						:clear-button-label="t('booking_start_date_clear')"
						data-testid="booking-start-date-input"
						class="resource-booking-modal-date-inputs__start-date-input"
						:format="dateFormat"
						append-to-body
						restore-date-on-close
						:disabled="isStartEndDateDisabled"
						:date-disabled="isStartDateDisabled"
						:label="t('resource-booking__timeframe')"
						:variant="fieldVariant('startDate')"
						@update:model-value="$emit('update:startDate', $event)"
					/>
					<AppDatePicker
						v-tooltip="dateDisabledTooltip"
						:model-value="booking.endDate"
						:clear-button-label="t('booking_end_date_clear')"
						data-testid="booking-end-date-input"
						class="resource-booking-modal-date-inputs__end-date-input"
						:date-disabled="isEndDateDisabled"
						:disabled="isStartEndDateDisabled"
						:format="dateFormat"
						append-to-body
						restore-date-on-close
						:variant="fieldVariant('endDate')"
						@update:model-value="$emit('update:endDate', $event)"
					/>
				</div>
				<div
					v-tooltip="totalWorkingDaysTooltip"
					class="resource-booking-modal-date-inputs__working-days"
				>
					{{ $t('resource_booking_working_days') }}:
					<span
						class="resource-booking-modal-date-inputs__working-days-text"
					>
						{{ workingDays }}
					</span>
				</div>
			</div>

			<div class="resource-booking-modal-date-inputs__inputs-column">
				<div
					class="resource-booking-modal-date-inputs__amount-select-wrapper"
				>
					<SuiNumberInput
						v-if="booking.unit === BookingAmountType.Percent"
						:model-value="booking.amountPercent"
						:label="t('resource-booking__amount')"
						class="resource-booking-modal-date-inputs__amount-input"
						:variant="fieldVariant('amountPercent')"
						@update:model-value="
							$emit('update:amountPercent', $event)
						"
						@blur="validateInputField('amountPercent')"
					/>
					<SuiTimeInput
						v-else
						:model-value="booking.amountTime"
						:label="t('resource-booking__amount')"
						class="resource-booking-modal-date-inputs__amount-input"
						:variant="fieldVariant('amountTime')"
						:hours-per-day="hoursPerDay"
						@update:model-value="$emit('update:amountTime', $event)"
						@blur="validateInputField('amountTime')"
					/>
					<SuiSelect
						:model-value="booking.unit"
						:aria-label="t('resource-booking__unit')"
						hide-clear-button
						:options="unitSelectOptions"
						class="resource-booking-modal-date-inputs__amount-type"
						@update:model-value="
							$emit('update:bookingUnit', $event)
						"
					>
						<template #no-results-content>
							{{ $t('search.noResults') }}
						</template>
					</SuiSelect>
				</div>
				<div
					v-tooltip="totalHoursTooltip"
					class="resource-booking-modal-date-inputs__total"
				>
					{{ resourceBookingTotalText }}
					<span
						class="resource-booking-modal-date-inputs__total-text"
					>
						{{ bookingDurationToDisplay }}
					</span>
				</div>
			</div>
		</div>
		<AppDatePicker
			v-else
			:model-value="splitDate"
			:clear-button-label="t('booking_split_date_clear')"
			:label="t('resource_planning_split_from_date')"
			:date-disabled="isSplitDateDisabled"
			:format="dateFormat"
			class="resource-booking-modal-date-inputs__split-date-input"
			append-to-body
			restore-date-on-close
			@update:model-value="$emit('update:splitDate', $event)"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { type Validation } from '@vuelidate/core';
import {
	SuiSelect,
	SuiTimeInput,
	SuiNumberInput,
	type SelectItem,
} from '@developers/scoro-ui';
import { AppDatePicker } from '@/features/App';
import { useFormatter, useTranslation } from '@/composables';
import { useSettings } from '@/composables/settings/useSettings';
import { Setting } from '@/constants/settings';
import { BookingAmountType, TYPES } from '@/generated/graphql';
import { type SAPIDate } from '@/types/sapi/scalars';

interface BookingData {
	startDate: SAPIDate;
	endDate: SAPIDate;
	amountPercent: string;
	amountTime: string;
	unit: BookingAmountType;
	selectedEntityType: typeof TYPES.User | typeof TYPES.RoleProject;
}

export interface ResourcePlanningBookingModalDateInputsProps {
	booking: BookingData;
	validator: Validation;
	isSplitMode: boolean;
	isStartEndDateDisabled: boolean;
	isStartDateDisabled: (date: SAPIDate) => boolean;
	isEndDateDisabled: (date: SAPIDate) => boolean;
	workingDays: number;
	bookingDurationToDisplay: string | undefined;
	splitDate: SAPIDate;
	isSplitDateDisabled: (date: SAPIDate) => boolean;
}

const props = defineProps<ResourcePlanningBookingModalDateInputsProps>();

defineEmits<{
	(e: 'update:startDate', value: SAPIDate): void;
	(e: 'update:endDate', value: SAPIDate): void;
	(e: 'update:splitDate', value: SAPIDate): void;
	(e: 'update:bookingUnit', value: BookingAmountType): void;
	(e: 'update:amountPercent', value: string): void;
	(e: 'update:amountTime', value: string): void;
}>();

const fieldHasError = (field: string): boolean => {
	return (
		props.validator[field] !== undefined &&
		props.validator[field].$error === true
	);
};

const fieldVariant = (field: string): 'error' | undefined => {
	return fieldHasError(field) ? 'error' : undefined;
};

const validateInputField = (field: string) => {
	props.validator[field].$touch();
	props.validator[field].$commit();
};

const { dateFormatForSUIdatePicker: dateFormat } = useFormatter();
const { setting } = useSettings();
const { t } = useTranslation();

const hoursPerDay = computed(() =>
	Number(setting(Setting.SYSTEM_DAY_UNIT_SUB_VALUE))
);

const dateDisabledTooltip = computed(() =>
	props.isStartEndDateDisabled
		? t('resource_booking_modal_date_before_allowed')
		: null
);

const totalWorkingDaysTooltip = computed(() => {
	if (props.booking.selectedEntityType === TYPES.User) {
		return t('resource-booking__total-workdays-individual-tooltip');
	}

	if (props.booking.selectedEntityType === TYPES.RoleProject) {
		return t('resource-booking__total-workdays-placeholder-tooltip');
	}

	return '';
});

const totalHoursTooltip = computed(() => {
	if (props.booking.unit === BookingAmountType.Percent) {
		if (props.booking.selectedEntityType === TYPES.User) {
			return t('resource-booking__total-hours-individual-tooltip');
		}

		if (props.booking.selectedEntityType === TYPES.RoleProject) {
			return t('resource-booking__total-hours-placeholder-tooltip');
		}
	}

	return '';
});

const unitSelectOptions: SelectItem[] = [
	{
		id: BookingAmountType.Percent,
		title: t('resource-booking__percent-per-day').toLowerCase(),
	},
	{
		id: BookingAmountType.Hour,
		title: t('resource-booking__hours-per-day').toLowerCase(),
	},
	{
		id: BookingAmountType.Total,
		title: t('resource-booking__hours-total').toLowerCase(),
	},
];

const resourceBookingTotalText = computed(() =>
	props.booking.unit === BookingAmountType.Total
		? t('resource-booking__per-day-duration')
		: t('resource-booking__total-duration')
);
</script>

<style lang="scss" scoped>
.resource-booking-modal-date-inputs {
	&__booking-inputs {
		display: flex;
		flex-direction: column;
		gap: 2.5rem;
	}

	&__inputs-column {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	&__date-select-wrapper,
	&__amount-select-wrapper {
		display: inline-flex;
		border: 1px solid $neutral-40;
		border-radius: 0.5rem;

		:deep(.sui-input) {
			border-style: none;
		}

		:deep(.sui-input__placeholder-label) {
			max-width: none;
		}
	}

	&__start-date-input,
	&__end-date-input {
		width: 14rem;
		border-style: none;
	}

	&__start-date-input {
		border-right: 1px solid $neutral-40;
	}

	&__split-date-input {
		width: 14rem;
	}

	&__amount-input {
		width: 12rem;
	}

	&__amount-type {
		width: 16rem;
		border-left: 1px solid $neutral-40;
	}

	&__working-days {
		font-size: 1.375rem;
		color: $neutral-70;
	}

	&__working-days-text {
		font-weight: $scoro-fw-semi-bold;
		color: $neutral-90;
	}

	&__total {
		font-size: 1.375rem;
		color: $neutral-70;
	}

	&__total-text {
		font-weight: $scoro-fw-semi-bold;
		color: $neutral-90;
	}
}
</style>
